import { DestroyRef, inject, signal, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export type GlobalHeaderTitleCount = number | string;

export type GlobalHeaderTitleOverride = string;

export type GlobalHeaderLastBreadcrumbLabelOverride = string;

export type GlobalHeaderBreadcrumbLabelOverrideFromEnd = Readonly<{
  /** 1 = last breadcrumb, 2 = second last, ... */
  fromEndIndex: number;
  label: string;
}>;

export type GlobalHeaderAction = Readonly<{
  id: string;
  kind?: 'button' | 'icon';

  /** Visible label (for kind === 'button') */
  labelTranslateKey?: string;
  label?: string;

  /** Icon name (for kind === 'icon') */
  icon?: string;

  ariaLabelTranslateKey?: string;
  ariaLabel?: string;

  tooltipTranslateKey?: string;
  tooltip?: string;

  disabled?: boolean;
  visible?: boolean;

  onTrigger: () => void;
}>;

@Service()
export class GlobalHeaderExtrasService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _titleOverride = signal<GlobalHeaderTitleOverride | null>(null);
  readonly titleOverride = this._titleOverride.asReadonly();

  private readonly _titleCount = signal<GlobalHeaderTitleCount | null>(null);
  readonly titleCount = this._titleCount.asReadonly();

  private readonly _actions = signal<ReadonlyArray<GlobalHeaderAction>>([]);
  readonly actions = this._actions.asReadonly();

  private readonly _lastBreadcrumbLabelOverride =
    signal<GlobalHeaderLastBreadcrumbLabelOverride | null>(null);
  readonly lastBreadcrumbLabelOverride = this._lastBreadcrumbLabelOverride.asReadonly();

  private readonly _breadcrumbLabelOverridesFromEnd = signal<Record<string, string>>({});
  readonly breadcrumbLabelOverridesFromEnd = this._breadcrumbLabelOverridesFromEnd.asReadonly();

  constructor() {
    // Reset between pages so stale counts don't stick.
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        const currentPathKey = this.getUrlPathKey(this.router.url);
        const nextPathKey = this.getUrlPathKey(event.url);

        // Avoid clearing header extras when only query params change.
        if (currentPathKey === nextPathKey) {
          return;
        }

        this._titleOverride.set(null);
        this._titleCount.set(null);
        this._actions.set([]);
        this._lastBreadcrumbLabelOverride.set(null);
        this._breadcrumbLabelOverridesFromEnd.set({});
      });
  }

  private getUrlPathKey(url: string): string {
    const tree = this.router.parseUrl(url ?? '');
    tree.queryParams = {};
    tree.fragment = null;
    return this.router.serializeUrl(tree);
  }

  setTitleOverride(value: GlobalHeaderTitleOverride | null): void {
    if (typeof value !== 'string') {
      this._titleOverride.set(null);
      return;
    }

    const trimmed = value.trim();
    this._titleOverride.set(trimmed.length > 0 ? trimmed : null);
  }

  clearTitleOverride(): void {
    this._titleOverride.set(null);
  }

  setTitleCount(value: GlobalHeaderTitleCount | null): void {
    if (typeof value === 'number') {
      this._titleCount.set(Number.isFinite(value) ? value : null);
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      this._titleCount.set(trimmed.length > 0 ? trimmed : null);
      return;
    }

    this._titleCount.set(null);
  }

  clearTitleCount(): void {
    this._titleCount.set(null);
  }

  setActions(actions: ReadonlyArray<GlobalHeaderAction>): void {
    this._actions.set(Array.isArray(actions) ? actions : []);
  }

  upsertAction(action: GlobalHeaderAction): void {
    const current = this._actions();
    const index = current.findIndex(a => a.id === action.id);
    if (index === -1) {
      this._actions.set([...current, action]);
      return;
    }
    const next = current.slice();
    next[index] = action;
    this._actions.set(next);
  }

  removeAction(id: string): void {
    this._actions.set(this._actions().filter(a => a.id !== id));
  }

  clearActions(): void {
    this._actions.set([]);
  }

  setLastBreadcrumbLabelOverride(value: GlobalHeaderLastBreadcrumbLabelOverride | null): void {
    if (typeof value !== 'string') {
      this._lastBreadcrumbLabelOverride.set(null);
      return;
    }

    const trimmed = value.trim();
    this._lastBreadcrumbLabelOverride.set(trimmed.length > 0 ? trimmed : null);
  }

  clearLastBreadcrumbLabelOverride(): void {
    this._lastBreadcrumbLabelOverride.set(null);
  }

  setBreadcrumbLabelOverrideFromEnd(
    override: GlobalHeaderBreadcrumbLabelOverrideFromEnd | null
  ): void {
    if (!override) {
      return;
    }

    const fromEndIndex = Math.trunc(override.fromEndIndex);
    if (!Number.isFinite(fromEndIndex) || fromEndIndex < 1) {
      return;
    }

    const label = override.label?.trim?.() ?? '';
    if (label.length === 0) {
      this.clearBreadcrumbLabelOverrideFromEnd(fromEndIndex);
      return;
    }

    this._breadcrumbLabelOverridesFromEnd.update(current => ({
      ...current,
      [String(fromEndIndex)]: label,
    }));
  }

  clearBreadcrumbLabelOverrideFromEnd(fromEndIndex: number): void {
    const index = Math.trunc(fromEndIndex);
    if (!Number.isFinite(index) || index < 1) {
      return;
    }

    this._breadcrumbLabelOverridesFromEnd.update(current => {
      const key = String(index);
      if (!(key in current)) {
        return current;
      }

      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  clearBreadcrumbLabelOverridesFromEnd(): void {
    this._breadcrumbLabelOverridesFromEnd.set({});
  }
}
